import { privateApi } from '@/config/axios.config';

export interface CashFlowTransaction {
    id: string;
    transactionNumber: string;
    tenantId: string;
    createdBy: string;
    type: 'income' | 'expense';
    category: string;
    description: string;
    amount: number;
    currency: string;
    date: string;
    status: 'pending' | 'completed' | 'cancelled';
    relatedEntityId?: string;
    relatedEntityType?: string;
    reference?: string;
    paymentMethod?: string;
    notes?: string;
    metadata?: Record<string, any>;
    createdAt: string;
    updatedAt: string;
}

export interface CashFlowTransactionCreateRequest {
    type: 'income' | 'expense';
    category: string;
    description: string;
    amount: number;
    currency?: string;
    date: string;
    status?: 'pending' | 'completed' | 'cancelled';
    relatedEntityId?: string;
    relatedEntityType?: string;
    reference?: string;
    paymentMethod?: string;
    notes?: string;
    metadata?: Record<string, any>;
}

export interface CashFlowTransactionUpdateRequest {
    type?: 'income' | 'expense';
    category?: string;
    description?: string;
    amount?: number;
    currency?: string;
    date?: string;
    status?: 'pending' | 'completed' | 'cancelled';
    reference?: string;
    paymentMethod?: string;
    notes?: string;
    metadata?: Record<string, any>;
}

export interface CashFlowTransactionFilters {
    search?: string;
    type?: 'income' | 'expense';
    category?: string;
    status?: 'pending' | 'completed' | 'cancelled';
    startDate?: string;
    endDate?: string;
    minAmount?: number;
    maxAmount?: number;
    currency?: string;
    page?: number;
    limit?: number;
}

export interface CashFlowStats {
    totalIncome: number;
    totalExpenses: number;
    netCashFlow: number;
    transactionCount: number;
    incomeByCategory: Record<string, number>;
    expenseByCategory: Record<string, number>;
    monthlyFlow: Array<{
        month: string;
        income: number;
        expenses: number;
        net: number;
    }>;
}

export interface CashFlowTransactionListResponse {
    data: CashFlowTransaction[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

const BASE_PATH = '/financial/transactions';

class TransactionApiService {
    // Obtener todas las transacciones con filtros
    async getTransactions(
        filters: CashFlowTransactionFilters = {}
    ): Promise<CashFlowTransactionListResponse> {
        const response = await privateApi.get(BASE_PATH, { params: filters });
        return response.data;
    }

    // Obtener transacción por ID
    async getTransaction(id: string): Promise<CashFlowTransaction> {
        const response = await privateApi.get(`${BASE_PATH}/${id}`);
        return response.data;
    }

    // Crear nueva transacción
    async createTransaction(
        transaction: CashFlowTransactionCreateRequest
    ): Promise<CashFlowTransaction> {
        const response = await privateApi.post(BASE_PATH, transaction);
        return response.data;
    }

    // Actualizar transacción
    async updateTransaction(
        id: string,
        updates: CashFlowTransactionUpdateRequest
    ): Promise<CashFlowTransaction> {
        const response = await privateApi.patch(`${BASE_PATH}/${id}`, updates);
        return response.data;
    }

    // Eliminar transacción
    async deleteTransaction(id: string): Promise<void> {
        await privateApi.delete(`${BASE_PATH}/${id}`);
    }

    // Obtener estadísticas
    async getStats(startDate?: string, endDate?: string): Promise<CashFlowStats> {
        const response = await privateApi.get(`${BASE_PATH}/stats`, {
            params: { startDate, endDate },
        });
        return response.data;
    }
}

export const transactionApiService = new TransactionApiService();
export default transactionApiService;
